import { useArchiveDownload, useCreateArchive, useExportFileDownload } from '@Hooks/Documents';
import { useScheduleExportRun } from '@Hooks/Exports';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { useDocumentsRepository } from '@Hooks/useRepository';
import { EnumArchiveRunState, EnumDynamicExportRunState } from '@Model';
import { ArchivesRepository, DynamicExportRunRepository } from '@Repositories/Documents';
import { isOk, type ListRequestWithIds, useToasts } from '@synqo/frontend-component-library';
import { exportRunResultMessages } from '@utils/exportRunResult';
import { describeApiMessage } from '@utils/parseApiMessages';
import { useCallback, useEffect, useRef, useState } from 'react';
import { describeInvoiceCount, type ExportFormat, isImmediateArchive } from './invoiceSnapshotExport';
import { INVOICE_SNAPSHOT_CATEGORY } from './useInvoiceSnapshotDocument';

const ARCHIVES = 'archives';
const EXPORT_RUNS = 'exports';

/** How long a spreadsheet or a small archive is waited for before the notification takes over. */
export const WAIT_LIMIT_MS = 60_000;

/** What became of a started export: waited for, announced by a toast, or refused. */
export type ExportOutcome = 'waiting' | 'announced' | 'refused';

export interface StartExportInput {
    format: ExportFormat;
    selection: ListRequestWithIds;
    /** How many invoices the selection covers, when known. Decides waiting versus announcing. */
    documentCount: number | undefined;
    /** The overview export definition, or undefined when this session is offered none. */
    excelDefinitionId: string | undefined;
}

interface WaitingRun {
    format: ExportFormat;
    /** The id of the export run, or of the archive for a ZIP. */
    runId: string;
    input: StartExportInput;
    /** Hands the run over to the notification once the wait limit is reached. */
    timer: ReturnType<typeof setTimeout>;
}

/**
 * Starts an invoice export. A spreadsheet and a small archive are waited for and downloaded, or left to the
 * notification once the wait limit is reached; either way `onExportSettled` is called. A large archive is
 * announced and left to the notification.
 *
 * Waiting asks the service right after the start, when it reports a changed archive or export run, and once
 * more at the wait limit in case a report got lost. Never on a schedule.
 */
// @ai-generated 2026-10-02T08:34:35+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function useInvoiceSnapshotExport(onExportSettled?: () => void) {
    const waiting = useRef<WaitingRun>(undefined);
    const [waitingFormat, setWaitingFormat] = useState<ExportFormat>();
    const startExportRef = useRef<(input: StartExportInput) => Promise<ExportOutcome>>(undefined);

    const { pushToast } = useToasts();
    const { createArchive, isCreating } = useCreateArchive();
    const { scheduleExport, isExporting } = useScheduleExportRun({ announce: false });
    const { downloadExportFile } = useExportFileDownload();
    const downloadArchive = useArchiveDownload();
    const archives = useDocumentsRepository(ArchivesRepository);
    const exportRuns = useDocumentsRepository(DynamicExportRunRepository);

    const announceBackgroundExport = useCallback(
        (format: ExportFormat, documentCount: number | undefined) => {
            const invoices = documentCount === undefined ? 'Ihre Rechnungen' : describeInvoiceCount(documentCount);
            const verb = documentCount === 1 ? 'wird' : 'werden';
            const what =
                format === 'excel'
                    ? 'Die Excel-Datei wird noch erstellt.'
                    : `${invoices} ${verb} als ZIP zusammengestellt.`;
            pushToast({
                title: 'Ihr Export wird erstellt …',
                message: `${what} Sie können weiterarbeiten, wir melden uns, sobald der Download bereit ist.`,
                variant: 'info',
                loading: true,
            });
        },
        [pushToast],
    );

    const stopWaiting = useCallback(() => {
        clearTimeout(waiting.current?.timer);
        waiting.current = undefined;
        setWaitingFormat(undefined);
    }, []);

    const offerRetry = useCallback(
        (input: StartExportInput, reason?: string) => {
            const file = input.format === 'excel' ? 'Die Excel-Datei' : 'Die ZIP-Datei';
            const why = reason === undefined ? '' : ` ${reason}`;
            pushToast({
                title: 'Export fehlgeschlagen',
                message: `${file} konnte nicht erstellt werden. Ihre Einstellungen bleiben gespeichert.${why}`,
                variant: 'error',
                actions: [
                    {
                        id: 'retry',
                        label: 'Erneut versuchen',
                        variant: 'error',
                        onClick: () => void startExportRef.current?.(input),
                    },
                ],
            });
        },
        [pushToast],
    );

    const deliverArchive = useCallback(
        async (archiveId: string) => {
            const outcome = await downloadArchive(archiveId);
            if (!outcome.ok) {
                pushToast({
                    title: 'Download fehlgeschlagen',
                    message: outcome.message ?? 'Das ZIP liegt unter Downloads bereit.',
                    variant: 'error',
                });
            }
            onExportSettled?.();
        },
        [downloadArchive, pushToast, onExportSettled],
    );

    const deliverFile = useCallback(
        async (fileId: string) => {
            await downloadExportFile(fileId);
            onExportSettled?.();
        },
        [downloadExportFile, onExportSettled],
    );

    /**
     * Looks at the run waited for and, once it has ended, delivers its file or offers to start it again. At the
     * wait limit a run that cannot be read is left to the notification.
     */
    const checkWaitingRun = useCallback(
        async (atWaitLimit?: boolean) => {
            const current = waiting.current;
            if (current === undefined) {
                return;
            }

            if (current.format === 'zip') {
                const result = await archives.describe(current.runId);
                if (waiting.current !== current) {
                    return;
                }
                const archive = isOk(result) ? result.data : undefined;
                if (archive?.state === EnumArchiveRunState.Finished) {
                    stopWaiting();
                    await deliverArchive(current.runId);
                } else if (archive?.state === EnumArchiveRunState.Failed) {
                    stopWaiting();
                    const [reason] = exportRunResultMessages(archive);
                    offerRetry(current.input, reason && describeApiMessage(reason));
                }
                return;
            }

            const result = await exportRuns.get(current.runId, undefined);
            if (waiting.current !== current) {
                return;
            }
            const run = isOk(result) ? result.data : undefined;
            if (!run) {
                if (!atWaitLimit) {
                    stopWaiting();
                    offerRetry(current.input);
                }
            } else if (run.state === EnumDynamicExportRunState.Finished && run.result_file_id) {
                stopWaiting();
                await deliverFile(run.result_file_id);
            } else if (run.state === EnumDynamicExportRunState.Failed) {
                stopWaiting();
                offerRetry(current.input);
            }
        },
        [archives, exportRuns, stopWaiting, deliverArchive, deliverFile, offerRetry],
    );

    const onArchivesChanged = useCallback(() => {
        if (waiting.current?.format === 'zip') {
            void checkWaitingRun();
        }
    }, [checkWaitingRun]);
    const onExportRunsChanged = useCallback(() => {
        if (waiting.current?.format === 'excel') {
            void checkWaitingRun();
        }
    }, [checkWaitingRun]);
    useRemoteCacheInvalidation(ARCHIVES, onArchivesChanged);
    useRemoteCacheInvalidation(EXPORT_RUNS, onExportRunsChanged);

    const waitFor = useCallback(
        (format: ExportFormat, runId: string, input: StartExportInput) => {
            clearTimeout(waiting.current?.timer);
            const timer = setTimeout(async () => {
                await checkWaitingRun(true).catch(() => undefined);
                if (waiting.current?.runId !== runId) {
                    return;
                }
                stopWaiting();
                announceBackgroundExport(format, input.documentCount);
                onExportSettled?.();
            }, WAIT_LIMIT_MS);
            waiting.current = { format, runId, input, timer };
            setWaitingFormat(format);
            void checkWaitingRun();
        },
        [stopWaiting, announceBackgroundExport, onExportSettled, checkWaitingRun],
    );

    const startExport = useCallback(
        async (input: StartExportInput): Promise<ExportOutcome> => {
            const { format, selection, documentCount, excelDefinitionId } = input;
            if (format === 'excel') {
                if (excelDefinitionId === undefined) {
                    return 'refused';
                }

                const scheduled = await scheduleExport({
                    exportDefinitionId: excelDefinitionId,
                    selectedIds: selection.include_ids ?? [],
                    filter: selection.filter_columns,
                    fullTextSearch: selection.full_text_search,
                });
                if (!scheduled.data) {
                    return 'refused';
                }

                waitFor(format, scheduled.data, input);
                return 'waiting';
            }

            const created = await createArchive({ print_option_id: INVOICE_SNAPSHOT_CATEGORY, selection });
            if (created === undefined) {
                return 'refused';
            }

            if (isImmediateArchive(documentCount)) {
                waitFor(format, created.archive_id, input);
                return 'waiting';
            }

            announceBackgroundExport(format, documentCount);
            return 'announced';
        },
        [scheduleExport, createArchive, announceBackgroundExport, waitFor],
    );
    startExportRef.current = startExport;

    useEffect(() => {
        const run = waiting;
        return () => {
            clearTimeout(run.current?.timer);
            run.current = undefined;
        };
    }, []);

    return {
        startExport,
        isStarting: isCreating || isExporting,
        isWaiting: waitingFormat !== undefined,
        waitingFormat,
    };
}
